import { useEffect } from 'react';
import { Query } from '@/utils/react-query/Queries';
import { setLatestEntitlements, getLatestEntitlements } from '@/utils/organizationEntitlements';

/**
 * Hydrated plan/entitlement state for the JWT organization.
 *
 * - `entitlements`: raw get_organization_entitlements payload, or null while
 *   loading/errored. `member === false` means no org/membership — never null.
 * - `resolved`: a positively hydrated membership response. Anything else
 *   (loading, error, member:false) is NOT Free — helpers fail open for
 *   display and the backend still enforces.
 *
 * Also mirrors the latest value into a module-level snapshot so non-React
 * consumers (e.g. recentlyVisited filtering) can resolve the current plan
 * synchronously.
 */
const useOrganizationEntitlements = ({ options } = {}) => {
    const { data, isPending, isSuccess, isError, refetch, isFetching } = Query.organizationEntitlements.current({ options });
    const entitlements = data ?? null;

    useEffect(() => {
        setLatestEntitlements(entitlements);
        // Many components mount this hook at once — only clear when the
        // snapshot is still the value this effect wrote, so an unmount can't
        // wipe a sibling's fresher entitlements.
        return () => {
            if (getLatestEntitlements() === entitlements) setLatestEntitlements(null);
        };
    }, [entitlements]);

    return {
        entitlements,
        resolved: isSuccess && entitlements?.member === true,
        isPending,
        isFetching,
        isError,
        refetch,
    };
};

export default useOrganizationEntitlements;
